import { useAutoneTranslation } from '@autone/translations';
import {
  AutoneTooltip,
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellTitle,
  DataTableHeaderCellWithSorting,
  DataTableIntegerValue,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';
import { Info } from 'lucide-react';
import { useMemo } from 'react';

import { type APP_NAME } from '../../../consts';
import { useAssortmentTableContext } from '../contexts';

const COLUMN_ID = 'warehouse_units' as const;

export type WarehouseUnitsColumnId = typeof COLUMN_ID;

interface WarehouseUnitsFields {
  parent_location_inventory_units?: number | null;
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<WarehouseUnitsFields>();

export const createWarehouseUnitsColumn = <
  T extends WarehouseUnitsFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.parent_location_inventory_units),
    {
      id: COLUMN_ID,
      size: 175,
      header: function WarehouseUnitsHeader({ column }) {
        const { t } = useAutoneTranslation<typeof APP_NAME>();
        const tableName = useAssortmentTableContext();

        const tooltipText = useMemo(() => {
          switch (tableName) {
            case 'productLocationsTable':
            case 'productLocationsSkuTable':
            case 'skuLocationsTable':
              return t(
                'table-column-tooltips.warehouse-units.product-locations-table',
              );
            default:
              throw new Error(
                `warehouse units column not supported by ${tableName}`,
              );
          }
        }, [tableName, t]);

        const headerCellContents = useMemo(
          () => (
            <DataTableCellContent align={'right'}>
              <DataTableCellTitle className="flex flex-row gap-2 items-center uppercase font-medium whitespace-normal text-right">
                {t('table-columns.warehouse-units')}
                <AutoneTooltip text={tooltipText}>
                  <Info className="inline" size={14} />
                </AutoneTooltip>
              </DataTableCellTitle>
            </DataTableCellContent>
          ),
          [tooltipText, t],
        );

        if (tableName === 'skuLocationsTable') return headerCellContents;

        return (
          <DataTableHeaderCellWithSorting
            column={column}
            arrowPosition="left"
            showSortable
          >
            {headerCellContents}
          </DataTableHeaderCellWithSorting>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function WarehouseUnitsCell({ getValue }) {
            return (
              <DataTableCellContent align="right">
                <DataTableCellTitle>
                  <DataTableIntegerValue value={getValue()} />
                </DataTableCellTitle>
              </DataTableCellContent>
            );
          },
        }),
    },
  );
